// theme
import aampeTheme from '../constants/aampeTheme';

// models
import { ModifiedChartDataset } from './charts.model';
import {
    METRIC_NAMES,
    MetricTimeFrame,
    SUCCESS_AND_PERFORMANCE_METRICS_WITH_COMPOSITE_METRIC,
} from './metrics.model';

export interface ResultsModel {
    [key: string]: { code: string; data: number | null };
}

export interface NumOfMessagesModel {
    [count: number]: string;
}

export interface MessagingFrequencyModel {
    [date: string]: NumOfMessagesModel;
}

export enum SchedulerStatus {
    SENT = 'SENT',
    IN_PROGRESS = 'IN PROGRESS',
    SCHEDULED = 'SCHEDULED',
}

export interface SchedulerModel {
    total_messages?: number | null;
    date: string;
    messaging_windows?: MessagingWindowModel[] | null;
    status?: TrackerStatusTypes;
}

export interface MessagingWindowModel {
    time_interval: string;
    message_count: number;
}

export enum SchedulingTimeWindow {
    NineAM = '09:00-12:00',
    Noon = '12:00-15:00',
    ThreePM = '15:00-18:00',
    SixPM = '18:00-21:00',
    NinePM = '21:00-00:00',
}

export enum DayOfWeek {
    Monday = 0,
    Tuesday = 1,
    Wednesday = 2,
    Thursday = 3,
    Friday = 4,
    Saturday = 5,
    Sunday = 6,
}

export interface WeekInProgressWindowModel {
    time_interval: SchedulingTimeWindow;
    current_week: number;
}

export interface WeekInProgressDateModel {
    day_of_week: DayOfWeek;
    status: TrackerStatusTypes;
    messaging_windows: WeekInProgressWindowModel[];
}

export interface WeekInProgressModel {
    current_week_dates: WeekInProgressDateModel[];
    previous_week_dates: WeekInProgressDateModel[];
    future_count: number;
}

export interface ModifiedWeekInProgressModel {
    datasets: ModifiedChartDataset[];
    labels: string[];
    future_count: number;
    totalScheduledCount: number;
    totalSentCount: number;
}

export enum TrackerStatusTypes {
    IN_PROGRESS = 'IN PROGRESS',
    IN_VALIDATION = 'IN VALIDATION',
    SCHEDULED = 'SCHEDULED',
    SENT = 'SENT',
    NOT_SCHEDULED = 'NOT SCHEDULED',
    EMPTY = 'EMPTY',
}

export type TrackerStatusModel = {
    name: string;
    color: string;
    borderColor: string;
    textColor: string;
    tooltipText?: string;
};

export const TrackerStatusMap: {
    [key in TrackerStatusTypes]: TrackerStatusModel;
} = {
    [TrackerStatusTypes.SENT]: {
        name: 'SENT',
        color: aampeTheme.colors.success500,
        borderColor: aampeTheme.colors.bordersWG03,
        textColor: aampeTheme.colors.success600,
    },
    [TrackerStatusTypes.IN_VALIDATION]: {
        name: 'IN VALIDATION',
        color: aampeTheme.colors.solidA01,
        borderColor: aampeTheme.colors.bordersWG03,
        textColor: aampeTheme.colors.blue700,
        tooltipText:
            'The message has been sent, but delivery has not been confirmed',
    },
    [TrackerStatusTypes.IN_PROGRESS]: {
        name: 'IN PROGRESS',
        color: aampeTheme.colors.solidSY01,
        borderColor: aampeTheme.colors.bordersWG03,
        textColor: aampeTheme.colors.solidSY01,
        tooltipText: 'The messages are being queued for sending',
    },
    [TrackerStatusTypes.SCHEDULED]: {
        name: 'SCHEDULED',
        color: aampeTheme.colors.blue,
        borderColor: aampeTheme.colors.bordersWG03,
        textColor: aampeTheme.colors.blue,
        tooltipText: 'Numbers are estimates and are subject to change',
    },
    [TrackerStatusTypes.NOT_SCHEDULED]: {
        name: 'NOT SCHEDULED',
        color: aampeTheme.colors.bordersWG01,
        borderColor: aampeTheme.colors.bordersWG03,
        textColor: aampeTheme.colors.textAccessibleWG01,
    },
    [TrackerStatusTypes.EMPTY]: {
        name: 'EMPTY',
        color: aampeTheme.colors.white,
        borderColor: aampeTheme.colors.white,
        textColor: aampeTheme.colors.white,
    },
};

export enum MonitoringTabs {
    Messaging = 'messaging',
    Events = 'events',
    Performance = 'performance',
}

export const MonitoringHeadings = {
    [MonitoringTabs.Messaging]: 'Messages sent',
    [MonitoringTabs.Events]: 'Events triggered',
    [MonitoringTabs.Performance]: 'Performance',
};

export enum MessagingInsights {
    PERSONALIZATION_FREQUENCY = 'personalization_frequency',
    SCHEDULER = 'scheduler',
    WEEK_IN_PROGRESS = 'week_in_progress',
}

export type Insights = MessagingInsights; // extend this as per needed

export type InsightsCardType = {
    value: Insights;
    label: string;
    title: string;
};

export type MonitoringTabInfo = {
    title: string;
    tooltip: string;
    description: string;
};

export enum MetricParams {
    none = 'none',
    formula = 'formula_id',
    channel = 'channel',
    label = 'label_id',
    userStatus = 'user_lifecycle_status',
    tag = 'tag_id',
}

export interface ChartMetricDefinitionModel {
    metric_name: string;
    display_name: string;
    time_frame: MetricTimeFrame;
    description?: string;
    iconUrl?: string;
}

export interface ChartParamsModel {
    param_code: MetricParams;
    display_name: string;
}

export interface MonitoringChartOptionsModel {
    metrics: ChartMetricDefinitionModel[];
    param_options: ChartParamsModel[];
}

export interface MonitoringChartFilterOptionsModel {
    code: string;
    display_name: string;
    description: string;
    metadata: { [key: string]: string };
}

export interface ChartsRequestParams {
    metric_name: string;
    time_frame: MetricTimeFrame;
    breakdown?: MetricParams;
    filter_by?: MetricParams;
    filter_detail?: string;
    start_date?: string;
    variant_type?: string;
    label_id?: string;
    is_highlight_weekend?: boolean;
}

export interface MonitoringChartsModel {
    data: { [key: string]: { [key: string]: string } };
    metadata: { [key: string]: { [key: string]: string } };
}

export const InsightsData: Record<MonitoringTabs, InsightsCardType[]> = {
    [MonitoringTabs.Messaging]: [
        {
            label: 'Track when your messages are being sent',
            value: MessagingInsights.SCHEDULER,
            title: 'Message scheduler',
        },
        {
            label: 'Learn how personalization frequency works',
            value: MessagingInsights.PERSONALIZATION_FREQUENCY,
            title: 'Frequency tracker',
        },
        {
            label: 'Shows the number of messages scheduled in each time window for the current week',
            value: MessagingInsights.WEEK_IN_PROGRESS,
            title: 'Week in progress',
        },
    ],
    [MonitoringTabs.Events]: [],
    [MonitoringTabs.Performance]: [],
};

export const MonitoringTabData: Record<MonitoringTabs, MonitoringTabInfo> = {
    [MonitoringTabs.Messaging]: {
        title: 'Messaging',
        description:
            'Use this page to monitor how many of which message(s) are being sent to which groups of your users and at which times and frequency. Key insights you can get from this page include messaging interests/trends over time and how this varies by audience, etc.',
        tooltip:
            'Click here to see the messages these different user populations are currently receiving.',
    },
    [MonitoringTabs.Events]: {
        title: 'Events',
        description:
            'This page gives you an idea of what your audience is doing regardless of your messaging. Think of it like the baseline of activity on your app. Use this tab to get an idea of general patterns or trends over time for your entire audience or selected cohorts. *Some turbulence is normal to see here, as many of these factors are driven by external factors (e.g. seasons, holidays, etc.)',
        tooltip:
            'Click here to get an idea of what your users are doing on your app.',
    },
    [MonitoringTabs.Performance]: {
        title: 'Performance',
        description:
            'This page is where it all comes together! See how your messaging is helping drive user behavior across multiple dimensions.',
        tooltip:
            'Click here to see the influence of these messages on your users.',
    },
};

interface QuickFilterByModel {
    mainFilter: MetricParams;
    filterDetail: string;
}

export interface QuickFilteringTabItemModel {
    question: string;
    showMe: string;
    breakDown?: MetricParams;
    representedBy?: string;
    filterBy?: QuickFilterByModel;
}

export type QuickFilteringTabDataModel = Record<
    MonitoringTabs,
    QuickFilteringTabItemModel[]
>;

enum MONITORING_REPRESENTED_BY_VALUES {
    SUCCESS_MESSAGE_SENT_PERCENTAGE = 'successful_messages_sent_percentage',
}

enum MONITORING_FILTER_DETAILS {
    ACTIVE = 'active',
    NEW = 'new',
}

export const QuickFilteringTabData: QuickFilteringTabDataModel = {
    [MonitoringTabs.Messaging]: [
        {
            question:
                'How many messages are being sent everyday for each channel?',
            showMe: METRIC_NAMES.NUM_MESSAGES_SENT_COUNT,
            breakDown: MetricParams.channel,
        },
        {
            question:
                'How many messages are being sent to New users vs Active users?',
            showMe: METRIC_NAMES.NUM_MESSAGES_SENT_COUNT,
            breakDown: MetricParams.userStatus,
        },
    ],
    [MonitoringTabs.Events]: [
        {
            question: `How many ${METRIC_NAMES.APP_VISITED_} are being driven by new users vs other users?`,
            showMe: METRIC_NAMES.APP_VISITED_EVENTS_COUNT,
            breakDown: MetricParams.userStatus,
        },
        {
            question: `What percentage of ${METRIC_NAMES.REVENUE_GENERATED_} are driven by new users?`,
            showMe: METRIC_NAMES.REVENUE_GENERATED_EVENTS_PERCENTAGE,
            breakDown: MetricParams.userStatus,
        },
        {
            question:
                'How many messages are being sent to New users vs Active users?',
            showMe: METRIC_NAMES.ADDED_TO_CART_EVENTS_PERCENTAGE,
            breakDown: MetricParams.userStatus,
        },
    ],
    [MonitoringTabs.Performance]: [
        {
            question:
                'How well are Aampe’s personalization models performing with regards to Add To Carts?',
            showMe: METRIC_NAMES.ADDED_TO_CART_,
            representedBy:
                MONITORING_REPRESENTED_BY_VALUES.SUCCESS_MESSAGE_SENT_PERCENTAGE,
        },
        {
            question:
                'What does personalization effectiveness look like for New users?',
            showMe: METRIC_NAMES.NOTIFICATION_OPENED_,
            representedBy:
                MONITORING_REPRESENTED_BY_VALUES.SUCCESS_MESSAGE_SENT_PERCENTAGE,
            filterBy: {
                mainFilter: MetricParams.userStatus,
                filterDetail: MONITORING_FILTER_DETAILS.NEW,
            },
        },
        {
            question: 'What about for Active Users?',
            showMe: METRIC_NAMES.NOTIFICATION_OPENED_,
            representedBy:
                MONITORING_REPRESENTED_BY_VALUES.SUCCESS_MESSAGE_SENT_PERCENTAGE,
            filterBy: {
                mainFilter: MetricParams.userStatus,
                filterDetail: MONITORING_FILTER_DETAILS.ACTIVE,
            },
        },
    ],
};

export type SuccessMetricsWithComposite = {
    [keys in SUCCESS_AND_PERFORMANCE_METRICS_WITH_COMPOSITE_METRIC]: number;
};
export interface ControlledFormulaMetricsModel
    extends SuccessMetricsWithComposite {
    formula_id: number;
    name: string;
    send_start?: Date;
}

export enum MonitoringPageToggleButtons {
    QUICK_FILTERING = 'quick_filtering',
    ADVANCED_FILTERS = 'advanced_filters',
    LEGEND = 'legend',
}
export enum MonitoringChartTypes {
    GLOBAL = 'global',
    FORMULA = 'formula',
    LABEL = 'label',
}

export enum FrequencyTrackerViewBy {
    LESS_THAN = 'less_than',
    EXACTLY = 'exactly',
    MORE_THAN = 'more_than',
}

export interface FrequencyTableModel {
    id: number;
    numOfMessages: number;
    [date: string]: number;
}
